/**
 * Web Audio implementation of the Channel One transformation.
 *
 * Implementation-conformant to the Broadcast #1 Realm Specification; explicitly
 * NON-CANONICAL (ENG-071). The specification is the source of truth for the sound;
 * this graph is replaceable by a shared engine build without product change.
 *
 * All processing is local. Visitor audio never leaves the machine (ENG-072).
 */
import { driftParams, type DriftParams } from './drift';

export class BroadcastEngine {
  #ctx: AudioContext | null = null;
  #buffer: AudioBuffer | null = null;
  #source: AudioBufferSourceNode | null = null;

  #dry!: GainNode;
  #wet!: GainNode;
  #out!: GainNode;
  #pre!: DelayNode;
  #verb!: ConvolverNode;
  #shelf!: BiquadFilterNode;
  #lowCut!: BiquadFilterNode;
  #floor!: GainNode;
  #floorSrc: AudioBufferSourceNode | null = null;

  // Transport state. AudioBufferSourceNode has no native pause, so position is
  // tracked manually and a fresh node is started from it on resume.
  #loop = true;
  #playing = false;
  #offset = 0;
  #anchorTime = 0;

  get ready(): boolean { return this.#buffer !== null; }
  get duration(): number { return this.#buffer?.duration ?? 0; }
  get playing(): boolean { return this.#playing; }
  /** null until init() has run. A realm sharing this context must await init() first. */
  get context(): AudioContext | null { return this.#ctx; }

  /** Created on the drop gesture — browsers require a user gesture to start audio. */
  async init(): Promise<void> {
    if (this.#ctx) return;
    const ctx = new AudioContext();
    this.#ctx = ctx;

    this.#out = ctx.createGain();
    this.#dry = ctx.createGain();
    this.#wet = ctx.createGain();
    this.#pre = ctx.createDelay(0.2);
    this.#verb = ctx.createConvolver();
    this.#verb.buffer = makeImpulse(ctx, 5.2);
    this.#shelf = ctx.createBiquadFilter();
    this.#shelf.type = 'highshelf';
    this.#lowCut = ctx.createBiquadFilter();
    this.#lowCut.type = 'highpass';
    this.#floor = ctx.createGain();
    this.#floor.gain.value = 0;

    // wet path: pre-delay -> convolver -> shaping
    this.#pre.connect(this.#verb);
    this.#verb.connect(this.#lowCut);
    this.#lowCut.connect(this.#shelf);
    this.#shelf.connect(this.#wet);
    this.#wet.connect(this.#out);
    this.#dry.connect(this.#out);
    this.#out.connect(ctx.destination);

    // interstellar floor rides the wet path only
    const floorSrc = ctx.createBufferSource();
    floorSrc.buffer = makeNoise(ctx, 4);
    floorSrc.loop = true;
    const floorFilter = ctx.createBiquadFilter();
    floorFilter.type = 'bandpass';
    floorFilter.frequency.value = 1400;
    floorFilter.Q.value = 0.4;
    floorSrc.connect(floorFilter);
    floorFilter.connect(this.#floor);
    this.#floor.connect(this.#wet);
    floorSrc.start();
    this.#floorSrc = floorSrc;

    this.apply(0);
  }

  /** Decode locally. The file never leaves the machine. */
  async load(file: File): Promise<void> {
    if (!this.#ctx) await this.init();
    const bytes = await file.arrayBuffer();
    this.#buffer = await this.#ctx!.decodeAudioData(bytes);
  }

  /** Hard (re)start from the top — a new broadcast, not a resume. */
  play(loop = true): void {
    const ctx = this.#ctx;
    if (!ctx || !this.#buffer) return;
    this.#loop = loop;
    this.#offset = 0;
    this.#startSource(0);
  }

  /** Continues from wherever playback last stopped or paused. */
  resume(): void {
    const ctx = this.#ctx;
    if (!ctx || !this.#buffer || this.#playing) return;
    this.#startSource(this.#offset);
  }

  pause(): void {
    const ctx = this.#ctx;
    if (!ctx || !this.#playing) return;
    this.#offset = (this.#offset + (ctx.currentTime - this.#anchorTime)) % this.#buffer!.duration;
    this.#stopSource();
    this.#playing = false;
  }

  stop(): void {
    this.#stopSource();
    this.#playing = false;
    this.#offset = 0;
  }

  #startSource(offset: number): void {
    const ctx = this.#ctx!;
    this.#stopSource();
    const src = ctx.createBufferSource();
    src.buffer = this.#buffer;
    src.loop = this.#loop;
    src.connect(this.#dry);
    src.connect(this.#pre);
    src.start(0, offset);
    this.#source = src;
    this.#anchorTime = ctx.currentTime;
    this.#playing = true;
  }

  #stopSource(): void {
    if (this.#source) {
      try { this.#source.stop(); } catch { /* already stopped */ }
      this.#source.disconnect();
      this.#source = null;
    }
  }

  /** Smoothed so no knob position produces a click or zipper artifact. */
  apply(knob: number): void {
    const ctx = this.#ctx;
    if (!ctx) return;
    const p: DriftParams = driftParams(knob);
    const t = ctx.currentTime;
    const TC = 0.04;
    this.#dry.gain.setTargetAtTime(p.dry, t, TC);
    this.#wet.gain.setTargetAtTime(p.wet, t, TC);
    this.#pre.delayTime.setTargetAtTime(p.preDelay, t, TC);
    this.#shelf.frequency.setTargetAtTime(p.highShelfHz, t, TC);
    this.#shelf.gain.setTargetAtTime(p.highShelfDb, t, TC);
    this.#lowCut.frequency.setTargetAtTime(p.lowCutHz, t, TC);
    this.#floor.gain.setTargetAtTime(p.floorLevel, t, TC);
  }

  /** A/B compare: level-honest, no loudness trick (ENG-025). */
  bypass(on: boolean): void {
    const ctx = this.#ctx;
    if (!ctx) return;
    const t = ctx.currentTime;
    this.#wet.gain.setTargetAtTime(on ? 0 : this.#wet.gain.value, t, 0.02);
    this.#dry.gain.setTargetAtTime(on ? 1 : this.#dry.gain.value, t, 0.02);
  }

  dispose(): void {
    this.stop();
    if (this.#floorSrc) { try { this.#floorSrc.stop(); } catch { /* noop */ } }
    void this.#ctx?.close();
    this.#ctx = null;
    this.#buffer = null;
  }
}

/** Decaying-noise impulse. Placeholder DSP, not canonical (ENG-071). */
function makeImpulse(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const ch = buf.getChannelData(c);
    for (let i = 0; i < len; i++) {
      ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
    }
  }
  return buf;
}

function makeNoise(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < len; i++) ch[i] = Math.random() * 2 - 1;
  return buf;
}
